import { Pagination } from 'components/Pagination/Pagination';
import { AvailabilityHelper } from 'lib/helper/availability/availability';
import { useDevice } from 'lib/hooks/useDevice';
import { usePagination } from 'lib/hooks/usePagination';
import {
  Availability,
  Month,
  SelectionAvailability,
} from 'lib/types/availability';
import { Duration } from 'lib/types/trip';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { AvailabilityCard } from './AvailabilityCard';
import { FlexibleDeparture } from './FlexibleDeparture';
import { FlexibleDepartureDecision } from 'lib/types/product';
import { useFlexibleDeparturePolicy } from 'lib/hooks/useFlexibleDeparturePolicy';
import { getIsSelected } from './helper';

export type Props = {
  avilabilities: Month[] | Availability[];
  selectedAvailability: SelectionAvailability;
  setTempSelectedAvailability: Dispatch<SetStateAction<SelectionAvailability>>;
  tempFlexibleDeparture: boolean;
  tempFlexibleDepartureDecision: FlexibleDepartureDecision | null;
  setTempFlexibleDeparture: Dispatch<SetStateAction<boolean>>;
  setTempFlexibleDepartureDecision: Dispatch<
    SetStateAction<FlexibleDepartureDecision | null>
  >;
  tempSelectedDuration: Duration | null;
  unit: string;
  tempSelectedMonth: Month | null;
};

function AvailabilityList({
  avilabilities,
  selectedAvailability,
  setTempSelectedAvailability,
  tempFlexibleDeparture,
  tempFlexibleDepartureDecision,
  setTempFlexibleDeparture,
  setTempFlexibleDepartureDecision,
  tempSelectedDuration,
  tempSelectedMonth,
  unit,
}: Props) {
  const { isMobile } = useDevice();
  const { allowsAsap } = useFlexibleDeparturePolicy();
  const availabilityListRef = useRef<HTMLDivElement>(null);
  const [isFakeLoading, setIsFakeLoading] = useState<boolean>(false);
  const pageSize = isMobile ? 4 : 3;
  const previousMonthStart = useRef(tempSelectedMonth?.startOfMonth);
  const previousDurationId = useRef(tempSelectedDuration?.id);

  const filteredAvailabilities = AvailabilityHelper.filterByDuration(
    avilabilities,
    tempSelectedDuration
  );

  const { current, pageCount, selectPage, currentPageItems } = usePagination<
    Month | Availability
  >({
    items: filteredAvailabilities,
    initialDefaultItem: selectedAvailability as Month | Availability,
    itemComparator: AvailabilityHelper.comparator,
    pageSize,
  });

  const selectPageHandler = (page: number) => {
    if (availabilityListRef.current) {
      availabilityListRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    selectPage(page);
    setIsFakeLoading(true);
    setTimeout(() => {
      setIsFakeLoading(false);
    }, 500);
  };

  const setSelectedAvailability = (availability: Month | Availability) => {
    setTempFlexibleDeparture(false);
    setTempFlexibleDepartureDecision(null);
    setTempSelectedAvailability(availability);
  };

  useEffect(() => {
    const monthStart = tempSelectedMonth?.startOfMonth;
    const durationId = tempSelectedDuration?.id;
    const isInitialDayMonthResolution =
      unit === 'Days' &&
      previousMonthStart.current === undefined &&
      monthStart !== undefined;
    const contextChanged =
      previousMonthStart.current !== monthStart ||
      previousDurationId.current !== durationId;

    previousMonthStart.current = monthStart;
    previousDurationId.current = durationId;

    if (contextChanged && !isInitialDayMonthResolution) {
      selectPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempSelectedMonth?.startOfMonth, tempSelectedDuration?.id, unit]);

  return (
    <div ref={availabilityListRef} className="flex flex-col">
      <div className="flex flex-col gap-2.5 sm:gap-x-2.5">
        <FlexibleDeparture
          decision="BNDL"
          title="Not sure yet?"
          subtitle="Book now, choose dates later"
          tempFlexibleDeparture={tempFlexibleDeparture}
          tempFlexibleDepartureDecision={tempFlexibleDepartureDecision}
          setTempFlexibleDeparture={setTempFlexibleDeparture}
          setTempFlexibleDepartureDecision={setTempFlexibleDepartureDecision}
        />
        {allowsAsap && (
          <FlexibleDeparture
            decision="ASAP"
            title="As soon as possible!"
            subtitle="We'll tell you the closest available date to go"
            tempFlexibleDeparture={tempFlexibleDeparture}
            tempFlexibleDepartureDecision={tempFlexibleDepartureDecision}
            setTempFlexibleDeparture={setTempFlexibleDeparture}
            setTempFlexibleDepartureDecision={setTempFlexibleDepartureDecision}
          />
        )}
      </div>
      <div className="flex flex-col sm:gap-x-2.5">
        {!isFakeLoading ? (
          currentPageItems.map((item, index) => (
            <AvailabilityCard
              key={index}
              availability={item}
              isSelected={
                getIsSelected(selectedAvailability, item) &&
                !tempFlexibleDeparture
              }
              select={setSelectedAvailability}
              unit={unit}
            />
          ))
        ) : (
          <>
            {[...new Array(pageSize)].map((_, index) => (
              <div
                key={index}
                className="rounded-md h-[3.875rem] flex flex-col items-stretch mt-2.5 bg-light-800 animate-pulse transition-colors"
              ></div>
            ))}
          </>
        )}
      </div>
      <div className="mt-5 w-full flex justify-center items-center">
        <Pagination
          count={pageCount}
          current={current}
          select={selectPageHandler}
        />
      </div>
    </div>
  );
}

export { AvailabilityList };
